import {
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ChartTable } from '../../components/charts/ChartTable.tsx';
import { formatNumber, formatPercent } from '../../lib/format.ts';
import { useLocale, useT } from '../../lib/i18n.tsx';
import type { CallPatternsResponse } from '../../lib/queries.ts';

/** Answered calls in the brand blue; unanswered in amber — a problem worth seeing, not an alarm. */
export const CALL_COLORS = {
  answered: 'var(--call-answered)',
  notAnswered: 'var(--call-missed)',
} as const;

/** Business hours shaded behind the bars (09:00–18:00). */
const WORK_START = 9;
const WORK_END = 17;

type Hour = CallPatternsResponse['data']['byHour'][number];

/**
 * Calls per local hour, stacked into answered and not answered, with the hour on the x axis as in
 * the old report. Hovering a bar shows the hour's totals and the inbound answer rate.
 */
export function HourlyCallsChart({ hours }: { hours: readonly Hour[] }) {
  const t = useT();
  const locale = useLocale();
  const h = t.home.hours;
  const data = hours.map((hour) => ({
    hour: hour.hour,
    answered: hour.answered,
    notAnswered: hour.total - hour.answered,
    source: hour,
  }));

  return (
    <figure aria-label={h.title} className="w-full">
      <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <LegendKey color={CALL_COLORS.answered} label={h.answered} />
        <LegendKey color={CALL_COLORS.notAnswered} label={h.notAnswered} />
        <span className="inline-flex items-center gap-1.5">
          <span aria-hidden="true" className="h-3 w-4 rounded-sm bg-muted ring-1 ring-border" />
          {h.workHours} {WORK_START}:00–{WORK_END + 1}:00
        </span>
      </div>
      <div className="h-64 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{ top: 8, right: 4, bottom: 0, left: 0 }}
            barCategoryGap={3}
          >
            <ReferenceArea
              x1={WORK_START}
              x2={WORK_END}
              fill="var(--muted)"
              fillOpacity={1}
              ifOverflow="extendDomain"
            />
            <CartesianGrid vertical={false} stroke="var(--border)" />
            <XAxis
              dataKey="hour"
              interval={0}
              tickLine={false}
              tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
            />
            <YAxis
              allowDecimals={false}
              width={40}
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
              tickFormatter={(value: number) => formatNumber(value, locale)}
            />
            <Tooltip
              isAnimationActive={false}
              cursor={{ fill: 'var(--accent)' }}
              content={({ active, payload }) => {
                const row = payload?.[0]?.payload as (typeof data)[number] | undefined;
                if (!active || !row) return null;
                return (
                  <div className="rounded-md border bg-card px-3 py-2 text-xs shadow-md">
                    <p className="font-semibold">{h.range(row.hour)}</p>
                    <p>
                      {formatNumber(row.source.total, locale)} {t.units.calls}
                    </p>
                    <p className="text-muted-foreground">
                      {h.answered}: {formatNumber(row.answered, locale)} · {h.notAnswered}:{' '}
                      {formatNumber(row.notAnswered, locale)}
                    </p>
                    <p className="text-muted-foreground">
                      {h.answerRate(formatPercent(row.source.answerRate, locale, 0))}
                    </p>
                  </div>
                );
              }}
            />
            <Bar
              dataKey="answered"
              name={h.answered}
              stackId="calls"
              fill={CALL_COLORS.answered}
              stroke="var(--card)"
              strokeWidth={1}
              isAnimationActive={false}
            />
            <Bar
              dataKey="notAnswered"
              name={h.notAnswered}
              stackId="calls"
              fill={CALL_COLORS.notAnswered}
              stroke="var(--card)"
              strokeWidth={1}
              radius={[3, 3, 0, 0]}
              isAnimationActive={false}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <ChartTable
        title={h.title}
        columns={[h.hour, t.common.total, h.answered, h.notAnswered]}
        rows={data.map((row) => [
          h.range(row.hour),
          formatNumber(row.source.total, locale),
          formatNumber(row.answered, locale),
          formatNumber(row.notAnswered, locale),
        ])}
      />
    </figure>
  );
}

export function LegendKey({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span aria-hidden="true" className="size-2.5 rounded-sm" style={{ background: color }} />
      {label}
    </span>
  );
}
